import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { toast } from "@/hooks/use-toast";

export interface Task {
  id: string;
  title: string;
  description?: string;
  status: "pending" | "completed";
  due_date?: string;
  assigned_user_id?: string;
  property_id?: string;
  tag_id?: string;
  sort_order: number;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  assigned_user?: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
  };
  property?: {
    id: string;
    title: string;
    short_name?: string;
  };
  tag?: {
    id: string;
    name: string;
    color: string;
  };
}

export interface SimpleTask {
  id: string;
  title: string;
  description?: string;
  status: "pending" | "completed";
  due_date?: string;
  assigned_user_id?: string;
  property_id?: string;
  tag_id?: string;
  sort_order: number;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface EnrichedTask extends SimpleTask {
  assigned_user?: {
    first_name: string;
    last_name: string;
    email?: string;
  };
  property?: {
    title: string;
    short_name?: string;
  };
  tag?: {
    name: string;
    color: string;
  };
}

export interface TaskTag {
  id: string;
  name: string;
  color: string;
  created_at: string;
  updated_at: string;
}

const useTasksRealtime = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("tasks-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tasks",
        },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: ["simple-tasks"] });
          queryClient.invalidateQueries({ queryKey: ["pending-tasks-count"] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};

export const useSimpleTasks = () => {
  useTasksRealtime();

  return useQuery({
    queryKey: ["simple-tasks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tasks")
        .select(
          "id, title, description, status, due_date, assigned_user_id, property_id, tag_id, sort_order, created_by_id, created_at, updated_at, completed_at",
        )
        .order("sort_order", { ascending: true });

      if (error) {
        console.error("Error fetching simple tasks:", error);
        throw error;
      }

      return (data || []) as SimpleTask[];
    },
    staleTime: 1000 * 60 * 2,
  });
};

//* TASKS - ENRICHED VIEW

export const useEnrichedTasks = () => {
  const {
    data: simpleTasks = [],
    isLoading: isLoadingTasks,
    isFetching: isFetchingTasks,
    error: errorTasks,
    refetch: refetchTasks,
  } = useSimpleTasks();

  const userIds = [
    ...new Set(
      simpleTasks.map((task) => task.assigned_user_id).filter(Boolean),
    ),
  ];
  const propertyIds = [
    ...new Set(simpleTasks.map((task) => task.property_id).filter(Boolean)),
  ];
  const tagIds = [
    ...new Set(simpleTasks.map((task) => task.tag_id).filter(Boolean)),
  ];

  const {
    data: users = {},
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    error: errorUsers,
    refetch: refetchUsers,
  } = useTaskUsers(userIds);

  const {
    data: properties = {},
    isLoading: isLoadingProperties,
    isFetching: isFetchingProperties,
    error: errorProperties,
    refetch: refetchProperties,
  } = useTaskProperties(propertyIds);

  const {
    data: tags = {},
    isLoading: isLoadingTags,
    isFetching: isFetchingTags,
    error: errorTags,
    refetch: refetchTags,
  } = useTaskTagsLookup(tagIds);

  const isLoading =
    isLoadingTasks || isLoadingUsers || isLoadingProperties || isLoadingTags;

  const isFetching =
    isFetchingTasks ||
    isFetchingUsers ||
    isFetchingProperties ||
    isFetchingTags;

  const error = errorTasks || errorUsers || errorProperties || errorTags;

  const refetchAll = async () => {
    await Promise.all([
      refetchTasks(),
      refetchUsers(),
      refetchProperties(),
      refetchTags(),
    ]);
  };

  const enrichedTasks: EnrichedTask[] = simpleTasks.map((task) => ({
    ...task,
    assigned_user: task.assigned_user_id
      ? users[task.assigned_user_id]
      : undefined,
    property: task.property_id ? properties[task.property_id] : undefined,
    tag: task.tag_id ? tags[task.tag_id] : undefined,
  }));

  return {
    data: enrichedTasks,
    isLoading,
    isFetching,
    error,
    refetch: refetchAll,
    internal: {
      isLoadingTasks,
      isLoadingUsers,
      isLoadingProperties,
      isLoadingTags,
      errorTasks,
      errorUsers,
      errorProperties,
      errorTags,
    },
  };
};

//* USERS LOOKUP (for EnrichedTask)

export const useTaskUsers = (userIds: string[]) => {
  return useQuery({
    queryKey: ["task-users", userIds],
    queryFn: async () => {
      if (userIds.length === 0) return {};

      const { data, error } = await supabase
        .from("user_profiles")
        .select("user_id, first_name, last_name, email")
        .in("user_id", userIds);

      if (error) throw error;

      return data.reduce((acc: any, user: any) => {
        acc[user.user_id] = user;
        return acc;
      }, {});
    },
    enabled: userIds.length > 0,
    staleTime: 1000 * 60 * 10,
  });
};

//* PROPERTIES LOOKUP (for EnrichedTask)

export const useTaskProperties = (propertyIds: string[]) => {
  return useQuery({
    queryKey: ["task-properties", propertyIds],
    queryFn: async () => {
      if (propertyIds.length === 0) return {};

      const { data, error } = await supabase
        .from("properties")
        .select("id, title, short_name")
        .in("id", propertyIds);

      if (error) throw error;

      return data.reduce((acc: any, property: any) => {
        acc[property.id] = property;
        return acc;
      }, {});
    },
    enabled: propertyIds.length > 0,
    staleTime: 1000 * 60 * 10,
  });
};

//* TAGS LOOKUP (for EnrichedTask)

export const useTaskTagsLookup = (tagIds: string[]) => {
  return useQuery({
    queryKey: ["task-tags-lookup", tagIds],
    queryFn: async () => {
      if (tagIds.length === 0) return {};

      const { data, error } = await supabase
        .from("task_tags")
        .select("id, name, color")
        .in("id", tagIds);

      if (error) throw error;

      return data.reduce((acc: any, tag: any) => {
        acc[tag.id] = tag;
        return acc;
      }, {});
    },
    enabled: tagIds.length > 0,
    staleTime: 1000 * 60 * 15,
  });
};

//* TAGS FULL LIST (for creation UI)

export const useTaskTags = () => {
  return useQuery({
    queryKey: ["task-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("task_tags")
        .select("*")
        .order("name", { ascending: true });

      if (error) {
        console.error("Error fetching task tags:", error);
        throw error;
      }

      return data as TaskTag[];
    },
  });
};

//* PENDING COUNT (for dashboard or badge)

export const usePendingTasksCount = () => {
  return useQuery({
    queryKey: ["pending-tasks-count"],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];

      const { count, error } = await supabase
        .from("tasks")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending")
        .eq("due_date", today);

      if (error) {
        console.error("Error fetching pending tasks count:", error);
        throw error;
      }

      return count || 0;
    },
    staleTime: 1000 * 60,
    refetchInterval: 1000 * 60 * 2,
  });
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      task: Omit<SimpleTask, "id" | "created_at" | "updated_at">,
    ) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("tasks")
        .insert({
          ...task,
          created_by_id: user.user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    onMutate: async (newTask) => {
      await queryClient.cancelQueries({ queryKey: ["simple-tasks"] });

      const previousTasks = queryClient.getQueryData<SimpleTask[]>([
        "simple-tasks",
      ]);

      const { data: user } = await supabase.auth.getUser();

      const optimisticTask: SimpleTask = {
        ...newTask,
        id: `temp-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by_id: user.user?.id ?? "",
        status: "pending",
        sort_order: 0,
      };

      queryClient.setQueryData<SimpleTask[]>(["simple-tasks"], (old = []) => [
        ...old,
        optimisticTask,
      ]);

      return { previousTasks };
    },

    onError: (error, _newTask, context) => {
      console.error("Error creating task:", error);
      toast({
        title: "Error",
        description: "No se pudo crear la tarea.",
        variant: "destructive",
      });

      if (context?.previousTasks) {
        queryClient.setQueryData(["simple-tasks"], context.previousTasks);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["simple-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["pending-tasks-count"] });
    },

    onSuccess: () => {
      toast({
        title: "Tarea creada",
        description: "La tarea se ha creado exitosamente.",
      });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<SimpleTask>;
    }) => {
      const { data, error } = await supabase
        .from("tasks")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: ["simple-tasks"] });

      const previousTasks = queryClient.getQueryData<SimpleTask[]>([
        "simple-tasks",
      ]);

      queryClient.setQueryData<SimpleTask[]>(["simple-tasks"], (old = []) =>
        old.map((task) => (task.id === id ? { ...task, ...updates } : task)),
      );

      return { previousTasks };
    },

    onError: (error, _variables, context) => {
      console.error("Error updating task:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la tarea.",
        variant: "destructive",
      });

      if (context?.previousTasks) {
        queryClient.setQueryData(["simple-tasks"], context.previousTasks);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["simple-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["pending-tasks-count"] });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tasks").delete().eq("id", id);
      if (error) throw error;
    },

    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["simple-tasks"] });

      const previousTasks = queryClient.getQueryData<SimpleTask[]>([
        "simple-tasks",
      ]);

      queryClient.setQueryData<SimpleTask[]>(["simple-tasks"], (old = []) =>
        old.filter((task) => task.id !== id),
      );

      return { previousTasks };
    },

    onError: (error, _id, context) => {
      console.error("Error deleting task:", error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la tarea.",
        variant: "destructive",
      });

      if (context?.previousTasks) {
        queryClient.setQueryData(["simple-tasks"], context.previousTasks);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["simple-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["pending-tasks-count"] });
    },

    onSuccess: () => {
      toast({
        title: "Tarea eliminada",
        description: "La tarea se ha eliminado exitosamente.",
      });
    },
  });
};

export const useCreateTaskTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      tag: Omit<TaskTag, "id" | "created_at" | "updated_at">,
    ) => {
      const { data, error } = await supabase
        .from("task_tags")
        .insert(tag)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-tags"] });
      toast({
        title: "Etiqueta creada",
        description: "La etiqueta se ha creado exitosamente.",
      });
    },
    onError: (error) => {
      console.error("Error creating task tag:", error);
      toast({
        title: "Error",
        description: "No se pudo crear la etiqueta.",
        variant: "destructive",
      });
    },
  });
};
